import React, { useEffect, useState, useCallback, useMemo } from "react";
import Layout2 from "../../components/global/layout2";
import ApiClient from "../../methods/api/apiClient";
import ReactECharts from "echarts-for-react";
import {
  Table,
  Card,
  Row,
  Col,
  Input,
  Button,
  Select,
  Tag,
  Spin,
  Empty,
  Tooltip,
  DatePicker,
  Modal,
  Descriptions,
  Badge,
} from "antd";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
import {
  FiSearch,
  FiXCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiDollarSign,
  FiBarChart2,
  FiActivity,
} from "react-icons/fi";

const { Option } = Select;

// -- Constants --
const PRIMARY = "#3E549D";
const SUCCESS = "#27ae60";
const WARNING = "#f39c12";
const DANGER = "#e74c3c";

const DAY_NAMES_FULL = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const DAY_NAMES_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const PERIOD_PRESETS = [
  { label: "Last 7 Days", days: 7 },
  { label: "Last Week", key: "lastWeek" },
  { label: "Last 30 Days", days: 30 },
  { label: "Last Month", key: "lastMonth" },
  { label: "3 Months", days: 90 },
  { label: "6 Months", days: 180 },
  { label: "12 Months", days: 365 },
];

// -- Style helpers --
const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  border: "none",
};

const getProbabilityColor = (pct) => {
  if (pct > 70) return SUCCESS;
  if (pct >= 40) return WARNING;
  return DANGER;
};

const getProbabilityBg = (pct) => {
  if (pct > 70) return "rgba(39,174,96,0.10)";
  if (pct >= 40) return "rgba(243,156,18,0.10)";
  return "rgba(231,76,60,0.10)";
};

const getConsistencyColor = (score) => {
  if (score > 70) return SUCCESS;
  if (score >= 40) return WARNING;
  return DANGER;
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return "0";
  return Number(n).toLocaleString();
};

const LaneIntelligence = () => {
  // -- Search state --
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, "month"));
  const [toDate, setToDate] = useState(dayjs());
  const [activePreset, setActivePreset] = useState("6 Months");
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");

  // -- Data state --
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  // -- Rate state --
  const [rateData, setRateData] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [loadStops, setLoadStops] = useState([]);
  const [stopsLoading, setStopsLoading] = useState(false);

  // Fetch boards on mount
  useEffect(() => {
    ApiClient.get("boards?status=active").then((res) => {
      if (res.success) {
        setBoards(res?.data?.data || []);
      }
    });
  }, []);

  // -- Search handler --
  const handleSearch = useCallback(() => {
    // Allow: origin-only, destination-only, or network-wide (neither)
    setLoading(true);
    setError(null);
    setSearched(true);
    setRateData(null);
    setRateError(null);

    const params = {};
    if (fromDate) params.startDate = fromDate.format("YYYY-MM-DD");
    if (toDate) params.endDate = toDate.format("YYYY-MM-DD");
    if (!fromDate && !toDate) params.months = 6;
    if (origin.trim()) params.origin = origin.trim();
    if (destination.trim()) params.destination = destination.trim();
    if (selectedBoard) params.board_id = selectedBoard;

    ApiClient.get("analytics/lane-intelligence", params).then((res) => {
      setLoading(false);
      if (res.success && res.data) {
        const d = res.data;
        // --- Transform backend response to UI format ---
        const ls = d.laneSummary || {};
        const fa = d.frequencyAnalysis || {};
        const dowd = d.dayOfWeekDistribution || {};
        const hodd = d.hourOfDayDistribution || {};
        const pp = d.postingPrediction || {};

        // 1. Summary: flat object for summary cards
        const dayDist = dowd.distribution || [];
        const peakDayEntry = [...dayDist].sort((a, b) => b.count - a.count)[0];
        const trendPct = fa.trend === 'increasing' ? 15 : fa.trend === 'decreasing' ? -15 : 0;

        const summary = {
          totalLoads: ls.totalLoads || 0,
          trend: trendPct,
          avgLoadsPerWeek: fa.avgLoadsPerWeek || 0,
          consistencyScore: pp.consistencyScore || 0,
          peakDay: dowd.mostActiveDay || (peakDayEntry ? peakDayEntry.day : 'N/A'),
          peakDayCount: peakDayEntry ? peakDayEntry.count : 0,
        };

        // 2. dayDistribution: object keyed by short day name { Mon: 260, Tue: 190, ... }
        const dayDistribution = {};
        dayDist.forEach((item) => {
          dayDistribution[item.day] = item.count;
        });

        // 3. hourDistribution: flat array of 24 numbers [count_for_hour_0, count_for_hour_1, ...]
        const hourArr = hodd.distribution || [];
        const hourDistribution = Array.from({ length: 24 }, (_, i) => {
          const found = hourArr.find((h) => h.hour === i);
          return found ? found.count : 0;
        });

        // 4. monthlyTrend: array with month label and count
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyTrend = (fa.monthly || []).map((m) => ({
          month: `${monthNames[m.month]} ${m.year}`,
          count: m.count,
        }));

        // 5. postingProbability: object keyed by day name { Mon: 85, Tue: 72, Sun: 60, ... }
        const postingProbability = {};
        (pp.currentWeekProbabilities || []).forEach((item) => {
          postingProbability[item.day] = item.probability;
        });

        // 6. topDestinations/topOrigins: map field names
        const mapLocations = (arr) =>
          (arr || []).map((item) => ({
            location: [item.city, item.state].filter(Boolean).join(', '),
            city: item.city,
            state: item.state,
            loads: item.count,
            avgDistance: item.avgDistance,
            lastPosted: item.lastSeen || null,
          }));
        const topDestinations = mapLocations(d.topDestinations);
        const topOrigins = mapLocations(d.topOrigins);

        // 8. topLanes (network-wide)
        const topLanes = (d.topLanes || []).map((item) => ({
          origin: [item.originCity, item.originState].filter(Boolean).join(', '),
          destination: [item.destCity, item.destState].filter(Boolean).join(', '),
          originCity: item.originCity,
          originState: item.originState,
          destCity: item.destCity,
          destState: item.destState,
          loads: item.count,
          avgDistance: item.avgDistance,
          lastPosted: item.lastSeen || null,
        }));

        // 7. nextPrediction
        const nlp = pp.nextLikelyPosting;
        const nextPrediction = nlp ? {
          day: nlp.day,
          time: nlp.hour != null ? `around ${nlp.hour > 12 ? nlp.hour - 12 : nlp.hour || 12}${nlp.hour >= 12 ? 'PM' : 'AM'}` : '',
          historicalCount: nlp.historicalCount,
        } : null;

        // 9. Pickup day/hour distributions
        const pdd = d.pickupDayDistribution || {};
        const phd = d.pickupHourDistribution || {};
        const pickupDayDist = {};
        (pdd.distribution || []).forEach((item) => {
          pickupDayDist[item.day] = item.count;
        });
        const pickupHourDist = Array.from({ length: 24 }, (_, i) => {
          const found = (phd.distribution || []).find((h) => h.hour === i);
          return found ? found.count : 0;
        });

        // 10. Recent loads
        const recentLoads = d.recentLoads || [];

        setData({
          summary,
          dayDistribution,
          hourDistribution,
          pickupDayDistribution: pickupDayDist,
          pickupHourDistribution: pickupHourDist,
          pickupPeakDay: pdd.peakDay || 'N/A',
          pickupPeakHour: phd.peakHour,
          monthlyTrend,
          postingProbability,
          topDestinations,
          topOrigins,
          topLanes,
          nextPrediction,
          recentLoads,
          schedulePatterns: d.schedulePatterns || [],
          originZip: ls.originZip,
          destinationZip: ls.destinationZip,
        });
      } else {
        setData(null);
        setError(res?.message || "No data found for this lane.");
      }
    });
  }, [origin, destination, fromDate, toDate, selectedBoard]);

  const handleClear = () => {
    setOrigin("");
    setDestination("");
    setFromDate(dayjs().subtract(6, "month"));
    setToDate(dayjs());
    setActivePreset("6 Months");
    setSelectedBoard("");
    setData(null);
    setSearched(false);
    setError(null);
    setRateData(null);
    setRateError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // -- Rate fetch --
  const openLoadModal = useCallback((record) => {
    setSelectedLoad(record);
    setLoadModalVisible(true);
    setLoadStops([]);
    if (record._id) {
      setStopsLoading(true);
      ApiClient.get("analytics/load-stops", { load_id: record._id }).then((res) => {
        setStopsLoading(false);
        if (res.success && res.stops) setLoadStops(res.stops);
      });
    }
  }, []);

  const fetchRates = useCallback(() => {
    // Prefer the ZIPs that the analytics aggregation extracted (most precise);
    // fall back to the raw user-typed text ("Chicago, IL" / "TX" / ZIP) so the
    // SONAR lookup still works when there are NO loads in history for the lane.
    const originText = (data?.originZip || origin || "").trim();
    const destText = (data?.destinationZip || destination || "").trim();
    if (!originText || !destText) return;

    // ZIP if it's all digits, otherwise free-form text → backend resolves both.
    const isZip = (v) => /^\d{3,5}$/.test(v);
    const params = {};
    if (isZip(originText)) params.origin_location_postal_code = originText;
    else params.origin = originText;
    if (isZip(destText)) params.destination_location_postal_code = destText;
    else params.destination = destText;

    setRateLoading(true);
    setRateError(null);
    ApiClient.get("rates", params).then((res) => {
      setRateLoading(false);
      if (res.success && res.data && (Array.isArray(res.data) ? res.data.length : true)) {
        setRateData(Array.isArray(res.data) ? res.data[0] : res.data);
      } else if (res.success) {
        setRateError(res?.message || "No SONAR rate data available for this lane.");
      } else {
        setRateError(res?.error || res?.message || "Unable to fetch rate data.");
      }
    });
  }, [data, origin, destination]);

  // -- Derived data --
  const summary = data?.summary || {};
  const dayDistribution = data?.dayDistribution || {};
  const hourDistribution = data?.hourDistribution || [];
  const monthlyTrend = data?.monthlyTrend || [];
  const postingProbability = data?.postingProbability || {};
  const topDestinations = data?.topDestinations || [];
  const topOrigins = data?.topOrigins || [];
  const topLanes = data?.topLanes || [];
  const nextPrediction = data?.nextPrediction || null;
  const pickupDayDistribution = data?.pickupDayDistribution || {};
  const pickupHourDistribution = data?.pickupHourDistribution || [];
  const recentLoads = data?.recentLoads || [];
  const schedulePatterns = data?.schedulePatterns || [];
  const [scheduleExpanded, setScheduleExpanded] = useState(false);
  const [scheduleLoadsModal, setScheduleLoadsModal] = useState(null); // { sp, dayNum, dayName, loads, loading }

  // Today's day index (0=Mon...6=Sun)
  const todayIndex = useMemo(() => {
    const d = new Date().getDay();
    return d === 0 ? 6 : d - 1; // JS: 0=Sun, convert to Mon-based
  }, []);

  // Probability boxes data
  const probabilityData = useMemo(() => {
    return DAY_NAMES_FULL.map((day, i) => {
      const shortDay = DAY_NAMES_SHORT[i];
      const prob = postingProbability[day] ?? postingProbability[shortDay] ?? null;
      const count = dayDistribution[day] ?? dayDistribution[shortDay] ?? 0;
      return {
        day,
        shortDay,
        probability: prob !== null ? Math.round(prob) : null,
        count,
        isToday: i === todayIndex,
      };
    });
  }, [postingProbability, dayDistribution, todayIndex]);

  // -- Chart: Day of Week Distribution --
  const dayChartOption = useMemo(() => {
    const counts = DAY_NAMES_SHORT.map(
      (d) => dayDistribution[d] ?? dayDistribution[DAY_NAMES_FULL[DAY_NAMES_SHORT.indexOf(d)]] ?? 0
    );
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params[0];
          return `<strong>${DAY_NAMES_FULL[p.dataIndex]}</strong><br/>Loads: ${p.value}`;
        },
      },
      grid: { left: "3%", right: "4%", bottom: "3%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: DAY_NAMES_SHORT,
        axisLabel: { fontSize: 12, color: "#666" },
        axisLine: { lineStyle: { color: "#ddd" } },
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11, color: "#999" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Loads",
          type: "bar",
          data: counts,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: PRIMARY },
                { offset: 1, color: "#6B7FC4" },
              ],
            },
            borderRadius: [6, 6, 0, 0],
          },
          barMaxWidth: 48,
          emphasis: {
            itemStyle: { color: "#2c3e7a" },
          },
        },
      ],
    };
  }, [dayDistribution]);

  // -- Chart: Hour of Day Distribution --
  const hourChartOption = useMemo(() => {
    const hours = Array.isArray(hourDistribution) ? hourDistribution : [];
    const hourLabels = Array.from({ length: 24 }, (_, i) => {
      if (i === 0) return "12 AM";
      if (i < 12) return `${i} AM`;
      if (i === 12) return "12 PM";
      return `${i - 12} PM`;
    });

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const p = params[0];
          return `<strong>${hourLabels[p.dataIndex]}</strong><br/>Loads: ${p.value}`;
        },
      },
      grid: { left: "3%", right: "4%", bottom: "8%", top: "12%", containLabel: true },
      xAxis: {
        type: "category",
        data: hourLabels,
        axisLabel: { fontSize: 10, color: "#666", rotate: 45 },
        axisLine: { lineStyle: { color: "#ddd" } },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11, color: "#999" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Loads",
          type: "line",
          data: hours.length === 24 ? hours : Array(24).fill(0),
          smooth: true,
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(62,84,157,0.25)" },
                { offset: 1, color: "rgba(62,84,157,0.02)" },
              ],
            },
          },
          lineStyle: { color: PRIMARY, width: 2.5 },
          itemStyle: { color: PRIMARY },
          symbol: "circle",
          symbolSize: 6,
          emphasis: {
            itemStyle: { color: "#2c3e7a", borderColor: "#fff", borderWidth: 2 },
          },
        },
      ],
    };
  }, [hourDistribution]);

  // -- Chart: Pickup Day of Week Distribution --
  const pickupDayChartOption = useMemo(() => {
    const counts = DAY_NAMES_SHORT.map(
      (d) => pickupDayDistribution[d] ?? pickupDayDistribution[DAY_NAMES_FULL[DAY_NAMES_SHORT.indexOf(d)]] ?? 0
    );
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const p = params[0];
          return `<strong>${DAY_NAMES_FULL[p.dataIndex]}</strong><br/>Loads picking up: ${p.value}`;
        },
      },
      grid: { left: "3%", right: "4%", bottom: "3%", top: "12%", containLabel: true },
      xAxis: { type: "category", data: DAY_NAMES_SHORT, axisLabel: { fontSize: 12, color: "#666" }, axisLine: { lineStyle: { color: "#ddd" } } },
      yAxis: { type: "value", axisLabel: { fontSize: 11, color: "#999" }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
      series: [{
        name: "Pickups",
        type: "bar",
        data: counts,
        itemStyle: {
          color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: SUCCESS }, { offset: 1, color: "#6bc48f" }] },
          borderRadius: [6, 6, 0, 0],
        },
        barMaxWidth: 48,
        emphasis: { itemStyle: { color: "#1a8a45" } },
      }],
    };
  }, [pickupDayDistribution]);

  // -- Chart: Pickup Hour of Day Distribution --
  const pickupHourChartOption = useMemo(() => {
    const hours = Array.isArray(pickupHourDistribution) ? pickupHourDistribution : [];
    const hourLabels = Array.from({ length: 24 }, (_, i) => {
      if (i === 0) return "12 AM";
      if (i < 12) return `${i} AM`;
      if (i === 12) return "12 PM";
      return `${i - 12} PM`;
    });
    return {
      tooltip: { trigger: "axis", formatter: (params) => { const p = params[0]; return `<strong>${hourLabels[p.dataIndex]}</strong><br/>Pickups: ${p.value}`; } },
      grid: { left: "3%", right: "4%", bottom: "8%", top: "12%", containLabel: true },
      xAxis: { type: "category", data: hourLabels, axisLabel: { fontSize: 10, color: "#666", rotate: 45 }, axisLine: { lineStyle: { color: "#ddd" } }, boundaryGap: false },
      yAxis: { type: "value", axisLabel: { fontSize: 11, color: "#999" }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
      series: [{
        name: "Pickups",
        type: "line",
        data: hours.length === 24 ? hours : Array(24).fill(0),
        smooth: true,
        areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(39,174,96,0.25)" }, { offset: 1, color: "rgba(39,174,96,0.02)" }] } },
        lineStyle: { color: SUCCESS, width: 2.5 },
        itemStyle: { color: SUCCESS },
        symbol: "circle",
        symbolSize: 6,
        emphasis: { itemStyle: { color: "#1a8a45", borderColor: "#fff", borderWidth: 2 } },
      }],
    };
  }, [pickupHourDistribution]);

  // -- Chart: Monthly Trend --
  const monthlyChartOption = useMemo(() => {
    const labels = monthlyTrend.map((m) => m.month || m.label || "");
    const values = monthlyTrend.map((m) => m.count ?? m.loads ?? 0);

    // Simple linear regression for trend line
    const n = values.length;
    let sumX = 0,
      sumY = 0,
      sumXY = 0,
      sumX2 = 0;
    for (let i = 0; i < n; i++) {
      sumX += i;
      sumY += values[i];
      sumXY += i * values[i];
      sumX2 += i * i;
    }
    const slope = n > 1 ? (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) : 0;
    const intercept = n > 0 ? (sumY - slope * sumX) / n : 0;
    const trendValues = values.map((_, i) => Math.max(0, Math.round((slope * i + intercept) * 100) / 100));

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
      },
      legend: {
        data: ["Loads", "Trend"],
        top: 0,
        textStyle: { fontSize: 12 },
      },
      grid: { left: "3%", right: "4%", bottom: "3%", top: "14%", containLabel: true },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: { fontSize: 11, color: "#666", rotate: labels.length > 12 ? 45 : 0 },
        axisLine: { lineStyle: { color: "#ddd" } },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 11, color: "#999" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Loads",
          type: "line",
          data: values,
          smooth: true,
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "rgba(62,84,157,0.20)" },
                { offset: 1, color: "rgba(62,84,157,0.01)" },
              ],
            },
          },
          lineStyle: { color: PRIMARY, width: 2.5 },
          itemStyle: { color: PRIMARY },
          symbol: "circle",
          symbolSize: 5,
        },
        {
          name: "Trend",
          type: "line",
          data: trendValues,
          lineStyle: { color: WARNING, width: 2, type: "dashed" },
          itemStyle: { color: WARNING },
          symbol: "none",
          smooth: false,
        },
      ],
    };
  }, [monthlyTrend]);

  // -- Click handler for drilling into a location --
  const handleLocationClick = useCallback((city, type) => {
    if (!city) return;
    if (type === 'destination') {
      setDestination(city);
      setOrigin(origin); // keep current origin
    } else if (type === 'origin') {
      setOrigin(city);
      setDestination(destination); // keep current destination
    }
    // Trigger search after state updates via setTimeout
    setTimeout(() => {
      document.getElementById('lane-search-btn')?.click();
    }, 100);
  }, [origin, destination]);

  // -- Network-wide: click a lane row to drill into that specific lane --
  const handleLaneClick = useCallback((record) => {
    setOrigin(record.origin || '');
    setDestination(record.destination || '');
    setTimeout(() => {
      document.getElementById('lane-search-btn')?.click();
    }, 100);
  }, []);

  // -- Top Destinations / Origins columns --
  const isNetworkWide = !origin.trim() && !destination.trim();
  const showTopDestinations = topDestinations.length > 0;
  const showTopOrigins = topOrigins.length > 0;
  const showTopLanes = topLanes.length > 0;
  const showTopTable = showTopDestinations || showTopOrigins || showTopLanes;

  const topTableData = showTopLanes ? topLanes : showTopDestinations ? topDestinations : topOrigins;
  const topTableTitle = showTopLanes
    ? "Top Lanes — Network Overview"
    : showTopDestinations && origin.trim()
    ? `Top Destinations from ${origin.trim()}`
    : showTopOrigins && destination.trim()
    ? `Top Origins to ${destination.trim()}`
    : showTopDestinations
    ? "Top Destinations"
    : "Top Origins";

  const topTableColumns = showTopLanes ? [
    {
      title: "Origin",
      dataIndex: "origin",
      key: "origin",
      render: (text, record) => (
        <span
          style={{ fontWeight: 600, cursor: "pointer", color: PRIMARY }}
          onClick={() => { setOrigin(text); setDestination(''); setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100); }}
        >
          <FiMapPin style={{ marginRight: 6 }} />
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
      render: (text, record) => (
        <span
          style={{ fontWeight: 600, cursor: "pointer", color: PRIMARY }}
          onClick={() => { setDestination(text); setOrigin(''); setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100); }}
        >
          <FiMapPin style={{ marginRight: 6 }} />
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Loads",
      dataIndex: "loads",
      key: "loads",
      sorter: (a, b) => (a.loads || 0) - (b.loads || 0),
      defaultSortOrder: "descend",
      render: (val) => <span style={{ fontWeight: 600 }}>{formatNumber(val)}</span>,
    },
    {
      title: "Avg Distance",
      dataIndex: "avgDistance",
      key: "avgDistance",
      sorter: (a, b) => (a.avgDistance || 0) - (b.avgDistance || 0),
      render: (val) => (val ? `${Math.round(val)} mi` : "N/A"),
    },
    {
      title: "Last Posted",
      dataIndex: "lastPosted",
      key: "lastPosted",
      render: (val) => {
        if (!val) return "N/A";
        const d = new Date(val);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      },
    },
  ] : [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text, record) => (
        <span
          style={{ fontWeight: 600, cursor: "pointer", color: PRIMARY, textDecoration: "underline dotted" }}
          onClick={() => {
            if (showTopDestinations && origin.trim()) {
              // Clicked a destination — set it as destination and search the specific lane
              setDestination(text);
              setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100);
            } else if (showTopOrigins && destination.trim()) {
              // Clicked an origin — set it as origin and search the specific lane
              setOrigin(text);
              setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100);
            } else {
              // Network-wide: clicking sets as origin to drill in
              setOrigin(text);
              setDestination('');
              setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100);
            }
          }}
        >
          <FiMapPin style={{ marginRight: 6, color: PRIMARY }} />
          {text || "N/A"}
        </span>
      ),
    },
    {
      title: "Loads",
      dataIndex: "loads",
      key: "loads",
      sorter: (a, b) => (a.loads || 0) - (b.loads || 0),
      defaultSortOrder: "descend",
      render: (val) => <span style={{ fontWeight: 600 }}>{formatNumber(val)}</span>,
    },
    {
      title: "Avg Distance",
      dataIndex: "avgDistance",
      key: "avgDistance",
      sorter: (a, b) => (a.avgDistance || 0) - (b.avgDistance || 0),
      render: (val) => (val ? `${Math.round(val)} mi` : "N/A"),
    },
    {
      title: "Last Posted",
      dataIndex: "lastPosted",
      key: "lastPosted",
      render: (val) => {
        if (!val) return "N/A";
        const d = new Date(val);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      },
    },
  ];

  // -- Render helpers --
  const renderSummaryCards = () => {
    const totalLoads = summary.totalLoads ?? 0;
    const trend = summary.trend ?? 0; // positive = up, negative = down
    const avgPerWeek = summary.avgLoadsPerWeek ?? 0;
    const consistency = summary.consistencyScore ?? 0;
    const peakDay = summary.peakDay || "N/A";
    const peakDayCount = summary.peakDayCount ?? 0;

    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>Total Loads</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#333", lineHeight: 1.1 }}>
                  {formatNumber(totalLoads)}
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: trend >= 0 ? "rgba(39,174,96,0.1)" : "rgba(231,76,60,0.1)",
                }}
              >
                {trend >= 0 ? (
                  <FiTrendingUp size={22} color={SUCCESS} />
                ) : (
                  <FiTrendingDown size={22} color={DANGER} />
                )}
              </div>
            </div>
            {trend !== 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: trend >= 0 ? SUCCESS : DANGER, fontWeight: 600 }}>
                {trend >= 0 ? "+" : ""}
                {trend}% vs prior period
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>Avg Loads/Week</div>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#333", lineHeight: 1.1 }}>
                  {typeof avgPerWeek === "number" ? avgPerWeek.toFixed(1) : avgPerWeek}
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(62,84,157,0.1)",
                }}
              >
                <FiBarChart2 size={22} color={PRIMARY} />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>Consistency Score</div>
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: getConsistencyColor(consistency),
                    lineHeight: 1.1,
                  }}
                >
                  {Math.round(consistency)}
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    consistency > 70
                      ? "rgba(39,174,96,0.1)"
                      : consistency >= 40
                      ? "rgba(243,156,18,0.1)"
                      : "rgba(231,76,60,0.1)",
                }}
              >
                <FiActivity size={22} color={getConsistencyColor(consistency)} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#f0f0f0",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, Math.max(0, consistency))}%`,
                    height: "100%",
                    borderRadius: 3,
                    backgroundColor: getConsistencyColor(consistency),
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card style={cardStyle} bodyStyle={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, color: "#999", marginBottom: 4 }}>Peak Day</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#333", lineHeight: 1.1 }}>
                  {peakDay}
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(243,156,18,0.1)",
                }}
              >
                <FiCalendar size={22} color={WARNING} />
              </div>
            </div>
            {peakDayCount > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
                {formatNumber(peakDayCount)} loads posted
              </div>
            )}
          </Card>
        </Col>
      </Row>
    );
  };

  const renderPostingProbability = () => {
    return (
      <Card
        style={{ ...cardStyle, marginBottom: 24 }}
        bodyStyle={{ padding: "24px" }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <FiClock size={20} color={PRIMARY} style={{ marginRight: 10 }} />
          <span style={{ fontSize: 18, fontWeight: 700, color: "#333" }}>
            Posting Probability This Week
          </span>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            overflowX: "auto",
            paddingBottom: 8,
          }}
        >
          {probabilityData.map((item) => {
            const hasProbability = item.probability !== null;
            const pct = hasProbability ? item.probability : 0;
            const color = hasProbability ? getProbabilityColor(pct) : "#ccc";
            const bg = hasProbability ? getProbabilityBg(pct) : "#fafafa";

            return (
              <div
                key={item.day}
                style={{
                  flex: "1 1 0",
                  minWidth: 110,
                  padding: "16px 12px",
                  borderRadius: 12,
                  backgroundColor: bg,
                  border: item.isToday ? `2.5px solid ${PRIMARY}` : "2px solid transparent",
                  textAlign: "center",
                  position: "relative",
                  transition: "all 0.2s",
                }}
              >
                {item.isToday && (
                  <Tag
                    color="blue"
                    style={{
                      position: "absolute",
                      top: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: 10,
                      lineHeight: "16px",
                      padding: "0 6px",
                    }}
                  >
                    TODAY
                  </Tag>
                )}
                <div style={{ fontSize: 13, fontWeight: 600, color: "#666", marginBottom: 8 }}>
                  {item.shortDay}
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: color,
                    lineHeight: 1.1,
                    marginBottom: 4,
                  }}
                >
                  {hasProbability ? `${pct}%` : "--"}
                </div>
                <div style={{ fontSize: 11, color: "#999" }}>
                  {formatNumber(item.count)} loads
                </div>
              </div>
            );
          })}
        </div>

        {nextPrediction && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 16px",
              borderRadius: 8,
              backgroundColor: "rgba(62,84,157,0.06)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FiClock size={16} color={PRIMARY} />
            <span style={{ fontSize: 14, color: "#444" }}>
              <strong>Next likely posting:</strong>{" "}
              {nextPrediction.day || ""} {nextPrediction.timeRange || nextPrediction.time || ""}
            </span>
          </div>
        )}
      </Card>
    );
  };

  const renderCharts = () => {
    const hasDayData = Object.values(dayDistribution).some((v) => v > 0);
    const hasHourData = Array.isArray(hourDistribution) && hourDistribution.some((v) => v > 0);
    const hasPickupDayData = Object.values(pickupDayDistribution).some((v) => v > 0);
    const hasPickupHourData = Array.isArray(pickupHourDistribution) && pickupHourDistribution.some((v) => v > 0);

    return (
      <>
        {/* POSTING distributions */}
        <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 12 }}>
          <span style={{ borderBottom: `3px solid ${PRIMARY}`, paddingBottom: 4 }}>When Loads Are Posted</span>
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card style={cardStyle} bodyStyle={{ padding: "20px 16px" }}
              title={<span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}><FiCalendar style={{ marginRight: 8, color: PRIMARY }} />Posted — Day of Week</span>}>
              {hasDayData ? <ReactECharts option={dayChartOption} style={{ height: 280 }} notMerge /> : <Empty description="No data" style={{ padding: "40px 0" }} />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card style={cardStyle} bodyStyle={{ padding: "20px 16px" }}
              title={<span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}><FiClock style={{ marginRight: 8, color: PRIMARY }} />Posted — Hour of Day</span>}>
              {hasHourData ? <ReactECharts option={hourChartOption} style={{ height: 280 }} notMerge /> : <Empty description="No data" style={{ padding: "40px 0" }} />}
            </Card>
          </Col>
        </Row>

        {/* PICKUP distributions */}
        <div style={{ fontSize: 16, fontWeight: 700, color: "#333", marginBottom: 12 }}>
          <span style={{ borderBottom: `3px solid ${SUCCESS}`, paddingBottom: 4 }}>When Loads Are Picked Up</span>
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card style={cardStyle} bodyStyle={{ padding: "20px 16px" }}
              title={<span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}><FiCalendar style={{ marginRight: 8, color: SUCCESS }} />Pickup — Day of Week</span>}>
              {hasPickupDayData ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {DAY_NAMES_FULL.map((day) => {
                    const short = DAY_NAMES_SHORT[DAY_NAMES_FULL.indexOf(day)];
                    const count = pickupDayDistribution[day] ?? pickupDayDistribution[short] ?? 0;
                    const totalPickup = Object.values(pickupDayDistribution).reduce((s, v) => s + v, 0) || 1;
                    const pct = Math.round((count / totalPickup) * 100);
                    const isMax = count === Math.max(...Object.values(pickupDayDistribution).filter(v => typeof v === "number"));
                    return (
                      <div key={day} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 6, background: isMax ? "#f0fdf4" : "transparent" }}>
                        <span style={{ fontWeight: 600, fontSize: 13, width: 80, color: isMax ? SUCCESS : "#333" }}>{day}</span>
                        <div style={{ flex: 1, height: 20, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: isMax ? SUCCESS : "#86efac", borderRadius: 4, transition: "width 0.3s" }} />
                        </div>
                        <span style={{ fontWeight: 700, fontSize: 14, width: 45, textAlign: "right", color: isMax ? SUCCESS : "#333" }}>{count}</span>
                        <span style={{ fontSize: 11, color: "#999", width: 35, textAlign: "right" }}>{pct}%</span>
                      </div>
                    );
                  })}
                  <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 6, borderTop: "1px solid #eee", marginTop: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>Total: {Object.values(pickupDayDistribution).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0)}</span>
                  </div>
                </div>
              ) : <Empty description="No pickup data" style={{ padding: "40px 0" }} />}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card style={cardStyle} bodyStyle={{ padding: "20px 16px" }}
              title={<span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}><FiClock style={{ marginRight: 8, color: SUCCESS }} />Pickup — Hour of Day</span>}>
              {hasPickupHourData ? <ReactECharts option={pickupHourChartOption} style={{ height: 280 }} notMerge /> : <Empty description="No pickup data" style={{ padding: "40px 0" }} />}
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderMonthlyTrend = () => {
    if (!monthlyTrend || monthlyTrend.length === 0) return null;

    return (
      <Card
        style={{ ...cardStyle, marginBottom: 24 }}
        bodyStyle={{ padding: "20px 16px" }}
        title={
          <span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}>
            <FiTrendingUp style={{ marginRight: 8, color: PRIMARY }} />
            Monthly Trend
          </span>
        }
      >
        <ReactECharts option={monthlyChartOption} style={{ height: 320 }} notMerge />
      </Card>
    );
  };

  const openScheduleLoads = async (sp, activeDay) => {
    setScheduleLoadsModal({ sp, dayNum: activeDay.dayNum, dayName: activeDay.day, loads: [], loading: true });
    try {
      const params = {
        hour: sp.pickupHour,
        minute: sp.pickupMinute,
        day_of_week: activeDay.dayNum,
      };
      if (sp.originCity) params.orig_city = sp.originCity;
      if (sp.originState) params.orig_state = sp.originState;
      if (sp.destCity) params.dest_city = sp.destCity;
      if (sp.destState) params.dest_state = sp.destState;
      if (fromDate) params.start_date = fromDate.format("YYYY-MM-DD");
      if (toDate) params.end_date = toDate.format("YYYY-MM-DD");

      const res = await ApiClient.get("analytics/schedule-loads", params);
      setScheduleLoadsModal((prev) => prev && ({ ...prev, loads: res?.loads || [], loading: false }));
    } catch (err) {
      setScheduleLoadsModal((prev) => prev && ({ ...prev, loads: [], loading: false }));
    }
  };

  const renderSchedulePatterns = () => {
    if (!schedulePatterns || schedulePatterns.length === 0) return null;

    const ALL_DAYS = ["S", "M", "T", "W", "T", "F", "S"];
    const ALL_DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const freqColors = { daily: SUCCESS, frequent: "#3b82f6", regular: "#8b5cf6" };
    const SHOW_LIMIT = 5;
    const collapsed = !scheduleExpanded;
    const displayData = collapsed ? schedulePatterns.slice(0, SHOW_LIMIT) : schedulePatterns;

    return (
      <div style={{ ...cardStyle, marginBottom: 16, background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: "#333", fontSize: 14 }}>
            <FiClock style={{ marginRight: 6, color: "#8b5cf6" }} />
            Pickup Schedules
            <span style={{ fontSize: 11, color: "#999", fontWeight: 400, marginLeft: 6 }}>({schedulePatterns.length})</span>
          </span>
        </div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: "#999", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <th style={{ textAlign: "left", padding: "0 4px 6px 0", fontWeight: 600 }}>Time</th>
              <th style={{ textAlign: "left", padding: "0 4px 6px", fontWeight: 600 }}>Days</th>
              <th style={{ textAlign: "left", padding: "0 4px 6px", fontWeight: 600 }}>Lane</th>
              <th style={{ textAlign: "right", padding: "0 4px 6px", fontWeight: 600 }}>#</th>
              <th style={{ textAlign: "center", padding: "0 0 6px 4px", fontWeight: 600 }}>Pattern</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((sp, idx) => {
              const color = freqColors[sp.frequency] || "#999";
              return (
                <tr key={idx} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "5px 4px 5px 0", fontWeight: 700, fontFamily: "monospace", color: PRIMARY, whiteSpace: "nowrap" }}>{sp.pickupTime}</td>
                  <td style={{ padding: "5px 4px" }}>
                    <div style={{ display: "flex", gap: 2 }}>
                      {ALL_DAYS.map((d, i) => {
                        const activeDay = (sp.activeDays || []).find(a => a.day === ALL_DAY_NAMES[i]);
                        const active = !!activeDay;
                        return (
                          <Tooltip key={i} title={active ? `Click to see ${activeDay.count} load${activeDay.count > 1 ? "s" : ""} on ${ALL_DAY_NAMES[i]}` : `No ${ALL_DAY_NAMES[i]} pickups`}>
                            <div
                              onClick={active ? () => openScheduleLoads(sp, activeDay) : undefined}
                              style={{
                                width: 18, height: 18, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center",
                                background: active ? color : "#f1f5f9", color: active ? "#fff" : "#d1d5db",
                                fontSize: 9, fontWeight: 700,
                                cursor: active ? "pointer" : "default",
                                transition: "transform 0.1s",
                              }}
                              onMouseEnter={(e) => { if (active) e.currentTarget.style.transform = "scale(1.15)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                            >{d}</div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </td>
                  <td style={{ padding: "5px 4px", color: "#444", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {sp.lane || "—"}
                  </td>
                  <td style={{ padding: "5px 4px", fontWeight: 700, textAlign: "right" }}>{sp.totalLoads}</td>
                  <td style={{ padding: "5px 0 5px 4px", textAlign: "center" }}>
                    <Tooltip title={sp.frequencyLabel === "Daily" ? "Every day" : sp.frequencyLabel?.startsWith("X") ? `Missing day ${sp.frequencyLabel.slice(1)} (1=Mon..7=Sun)` : `Only days ${sp.frequencyLabel?.slice(0, -1)} (1=Mon..7=Sun)`}>
                      <Tag color={color} style={{ fontSize: 10, fontWeight: 700, margin: 0, padding: "0 8px", lineHeight: "20px", fontFamily: "monospace" }}>
                        {sp.frequencyLabel || "?"}
                      </Tag>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {schedulePatterns.length > SHOW_LIMIT && (
          <div
            style={{ textAlign: "center", marginTop: 6, cursor: "pointer", color: PRIMARY, fontSize: 12, fontWeight: 600 }}
            onClick={() => setScheduleExpanded(!scheduleExpanded)}
          >
            {collapsed ? `Show all ${schedulePatterns.length} patterns ▾` : "Show less ▴"}
          </div>
        )}
      </div>
    );
  };

  const renderRecentLoads = () => {
    if (!recentLoads || recentLoads.length === 0) return null;

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "N/A";
    const fmtDateTime = (d) => d ? new Date(d).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "N/A";

    const cols = [
      {
        title: "Load ID",
        dataIndex: "load_id",
        key: "load_id",
        width: 110,
        render: (val) => <span style={{ fontWeight: 600, fontSize: 12, fontFamily: "monospace", color: PRIMARY, cursor: "pointer" }}>{val}</span>,
      },
      {
        title: "Origin",
        dataIndex: "origin",
        key: "origin",
        render: (text) => <span style={{ fontSize: 12 }}>{text || "N/A"}</span>,
      },
      {
        title: "Destination",
        dataIndex: "destination",
        key: "destination",
        render: (text) => <span style={{ fontSize: 12 }}>{text || "N/A"}</span>,
      },
      {
        title: "Posted",
        key: "posted",
        render: (_, r) => (
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>{r.postedAt ? new Date(r.postedAt).toLocaleDateString("en-US", { weekday: "short" }) : ""}</div>
            <div style={{ color: "#999" }}>{r.postedAt ? new Date(r.postedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}</div>
          </div>
        ),
        sorter: (a, b) => new Date(a.postedAt || 0) - new Date(b.postedAt || 0),
        defaultSortOrder: "descend",
      },
      {
        title: "Pickup",
        key: "pickup",
        render: (_, r) => {
          const dt = r.pickupDateStr || (r.pickupDate ? new Date(r.pickupDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null);
          const day = r.pickupDate ? new Date(r.pickupDate).toLocaleDateString("en-US", { weekday: "short" }) : "";
          return (
            <div style={{ fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: SUCCESS }}>{day}</div>
              <div style={{ color: "#999" }}>{dt || "N/A"}</div>
            </div>
          );
        },
        sorter: (a, b) => new Date(a.pickupDate || 0) - new Date(b.pickupDate || 0),
      },
      {
        title: "Distance",
        dataIndex: "distance",
        key: "distance",
        width: 90,
        render: (val) => val ? `${Math.round(val)} mi` : "N/A",
        sorter: (a, b) => (a.distance || 0) - (b.distance || 0),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (val) => {
          const colors = { delivered: SUCCESS, pending: WARNING, intransit: PRIMARY, pickedup: "#17a2b8" };
          return <Tag color={colors[val] || "#999"} style={{ fontSize: 11 }}>{(val || "N/A").toUpperCase()}</Tag>;
        },
      },
    ];

    const statusColors = { delivered: SUCCESS, pending: WARNING, intransit: PRIMARY, pickedup: "#17a2b8", cancelled: "#ff4d4f" };
    const sl = selectedLoad || {};

    return (
      <>
        <Card
          style={{ ...cardStyle, marginBottom: 24 }}
          bodyStyle={{ padding: "20px" }}
          title={
            <span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}>
              <FiCalendar style={{ marginRight: 8, color: PRIMARY }} />
              Recent Loads ({recentLoads.length})
            </span>
          }
        >
          <Table
            columns={cols}
            dataSource={recentLoads}
            rowKey={(r, i) => (r.load_id || "") + "_" + i}
            pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ["10", "15", "25", "50"] }}
            size="small"
            scroll={{ x: 800 }}
            locale={{ emptyText: <Empty description="No loads found" /> }}
            onRow={(record) => ({
              onClick: () => openLoadModal(record),
              style: { cursor: "pointer" },
            })}
          />
        </Card>

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>Load Details</span>
              <Tag color={PRIMARY} style={{ fontSize: 13, fontFamily: "monospace", fontWeight: 600 }}>{sl.load_id}</Tag>
              {sl.status && <Badge color={statusColors[sl.status] || "#999"} text={<span style={{ fontSize: 12, fontWeight: 600 }}>{(sl.status || "").toUpperCase()}</span>} />}
            </div>
          }
          open={loadModalVisible}
          onCancel={() => { setLoadModalVisible(false); setSelectedLoad(null); setLoadStops([]); }}
          footer={null}
          width={750}
          bodyStyle={{ padding: "16px 24px", maxHeight: "75vh", overflowY: "auto" }}
        >
          {selectedLoad && (
            <div>
              {/* Route Section — Origin */}
              <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginBottom: 12 }}>
                <Row gutter={16}>
                  {(() => {
                    const firstStop = loadStops[0];
                    const lastStop = loadStops.length > 0 ? loadStops[loadStops.length - 1] : null;
                    const originCityFromStop = firstStop ? [firstStop.city, firstStop.state].filter(Boolean).join(", ") : null;
                    const destCityFromStop = lastStop ? [lastStop.city, lastStop.state].filter(Boolean).join(", ") : null;
                    const originAddrFromStop = firstStop ? firstStop.address : null;
                    const destAddrFromStop = lastStop ? lastStop.address : null;
                    const originZipFromStop = firstStop ? firstStop.postalCode : null;
                    const destZipFromStop = lastStop ? lastStop.postalCode : null;
                    const originFacilityFromStop = firstStop ? firstStop.facilityCode : null;
                    const destFacilityFromStop = lastStop ? lastStop.facilityCode : null;
                    const origin = sl.origin || originCityFromStop;
                    const destination = sl.destination || destCityFromStop;
                    const originAddress = sl.originAddress || originAddrFromStop;
                    const destinationAddress = sl.destinationAddress || destAddrFromStop;
                    const originZip = sl.originZip || originZipFromStop;
                    const destinationZip = sl.destinationZip || destZipFromStop;
                    const originFacility = sl.originFacility || originFacilityFromStop;
                    const destinationFacility = sl.destinationFacility || destFacilityFromStop;
                    return (
                      <>
                        <Col span={11}>
                          <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Origin</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{origin || "—"}</div>
                          {originAddress && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{originAddress}</div>}
                          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                            <span style={{ fontSize: 11, color: "#999" }}>ZIP: {originZip || "—"}</span>
                            <span style={{ fontSize: 11, color: originFacility ? PRIMARY : "#999", fontWeight: 600 }}>
                              Facility: {originFacility || "—"}
                            </span>
                          </div>
                        </Col>
                        <Col span={2} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <span style={{ fontSize: 20, color: PRIMARY }}>→</span>
                        </Col>
                        <Col span={11}>
                          <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Destination</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#333" }}>{destination || "—"}</div>
                          {destinationAddress && <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{destinationAddress}</div>}
                          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                            <span style={{ fontSize: 11, color: "#999" }}>ZIP: {destinationZip || "—"}</span>
                            <span style={{ fontSize: 11, color: destinationFacility ? PRIMARY : "#999", fontWeight: 600 }}>
                              Facility: {destinationFacility || "—"}
                            </span>
                          </div>
                        </Col>
                      </>
                    );
                  })()}
                </Row>
              </div>

              {/* Intermediary Stops */}
              {stopsLoading && <div style={{ textAlign: "center", padding: 8 }}><Spin size="small" /> Loading stops...</div>}
              {loadStops.length > 0 && (
                <div style={{ background: "#fffbe6", borderRadius: 8, padding: 12, marginBottom: 12, border: "1px solid #ffe58f" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#d48806", marginBottom: 8 }}>
                    <FiMapPin style={{ marginRight: 4 }} /> Intermediary Stops ({loadStops.length})
                  </div>
                  {loadStops.map((stop, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderBottom: idx < loadStops.length - 1 ? "1px dashed #ffe58f" : "none" }}>
                      <Tag color="orange" style={{ fontSize: 11, minWidth: 32, textAlign: "center" }}>{stop.stopNo}</Tag>
                      <span style={{ fontSize: 12, color: "#333" }}>{stop.address || [stop.city, stop.state, stop.postalCode].filter(Boolean).join(", ") || "N/A"}</span>
                      {stop.facilityCode && <Tag style={{ fontSize: 10, marginLeft: "auto" }}>{stop.facilityCode}</Tag>}
                    </div>
                  ))}
                </div>
              )}

              {/* Dates & Times Section */}
              <Descriptions bordered size="small" column={2} style={{ marginBottom: 12 }}
                labelStyle={{ fontWeight: 600, fontSize: 12, background: "#fafafa", width: 130 }}
                contentStyle={{ fontSize: 12 }}
              >
                <Descriptions.Item label="Posted">{fmtDateTime(sl.postedAt)}</Descriptions.Item>
                <Descriptions.Item label="Distance">{sl.distance ? `${Math.round(sl.distance)} miles` : "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Pickup">
                  <div>
                    <span style={{ fontWeight: 600, color: SUCCESS }}>{sl.pickupDateStr || fmtDate(sl.pickupDate)}</span>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Delivery">
                  <div>
                    <span style={{ fontWeight: 600, color: "#e67e22" }}>{sl.deliveryDateStr || fmtDate(sl.deliveryDate)}</span>
                  </div>
                </Descriptions.Item>
              </Descriptions>

              {/* Load Details Section */}
              <Descriptions bordered size="small" column={2} style={{ marginBottom: 12 }}
                labelStyle={{ fontWeight: 600, fontSize: 12, background: "#fafafa", width: 130 }}
                contentStyle={{ fontSize: 12 }}
              >
                {sl.customer && <Descriptions.Item label="Customer">{sl.customer}</Descriptions.Item>}
                {sl.equipment && <Descriptions.Item label="Equipment">{sl.equipment}</Descriptions.Item>}
                {sl.loadSize && <Descriptions.Item label="Load Size">{sl.loadSize}</Descriptions.Item>}
                {sl.weight ? <Descriptions.Item label="Weight">{Math.round(sl.weight).toLocaleString()} lbs</Descriptions.Item> : null}
                {sl.pallets ? <Descriptions.Item label="Pallets">{sl.pallets}</Descriptions.Item> : null}
                {sl.pieces ? <Descriptions.Item label="Pieces">{sl.pieces}</Descriptions.Item> : null}
                {sl.volume ? <Descriptions.Item label="Volume">{sl.volume} cu ft</Descriptions.Item> : null}
                {sl.rate ? <Descriptions.Item label="Rate"><span style={{ fontWeight: 700, color: SUCCESS }}>${parseFloat(sl.rate).toLocaleString()}</span></Descriptions.Item> : null}
                {sl.commodity && <Descriptions.Item label="Commodity" span={2}>{sl.commodity}</Descriptions.Item>}
                {sl.dimensions && <Descriptions.Item label="Dimensions">{sl.dimensions}</Descriptions.Item>}
                {sl.bidCount != null && <Descriptions.Item label="Bids">{sl.bidCount}</Descriptions.Item>}
                {sl.postedBy && <Descriptions.Item label="Posted By">{sl.postedBy}</Descriptions.Item>}
                {sl.carrier && <Descriptions.Item label="Carrier">{sl.carrier}</Descriptions.Item>}
              </Descriptions>

              {/* Notes / Memo / Special Instructions */}
              {(sl.notes || sl.memo || sl.specialInstructions) && (
                <div style={{ marginTop: 8 }}>
                  {sl.memo && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4 }}>Memo</div>
                      <div style={{ fontSize: 12, color: "#333", background: "#f8fafc", padding: "8px 12px", borderRadius: 6 }}>{sl.memo}</div>
                    </div>
                  )}
                  {sl.notes && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4 }}>Notes</div>
                      <div style={{ fontSize: 12, color: "#333", background: "#f8fafc", padding: "8px 12px", borderRadius: 6 }}>{sl.notes}</div>
                    </div>
                  )}
                  {sl.specialInstructions && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4 }}>Special Instructions</div>
                      <div style={{ fontSize: 12, color: "#333", background: "#fff7e6", padding: "8px 12px", borderRadius: 6, border: "1px solid #ffe7ba" }}>{sl.specialInstructions}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </>
    );
  };

  const renderTopTable = () => {
    if (!showTopTable) return null;

    // For network-wide mode with topLanes, show lanes table + side-by-side origins/destinations
    if (showTopLanes) {
      const miniLocCols = [
        {
          title: "Location",
          dataIndex: "location",
          key: "location",
          render: (text) => (
            <span
              style={{ fontWeight: 600, cursor: "pointer", color: PRIMARY, textDecoration: "underline dotted" }}
              onClick={() => { setOrigin(text); setDestination(''); setTimeout(() => document.getElementById('lane-search-btn')?.click(), 100); }}
            >
              <FiMapPin style={{ marginRight: 4, fontSize: 12 }} />{text || "N/A"}
            </span>
          ),
        },
        {
          title: "Loads",
          dataIndex: "loads",
          key: "loads",
          render: (val) => <span style={{ fontWeight: 600 }}>{formatNumber(val)}</span>,
        },
      ];

      return (
        <>
          <Card
            style={{ ...cardStyle, marginBottom: 24 }}
            bodyStyle={{ padding: "20px" }}
            title={
              <span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}>
                <FiMapPin style={{ marginRight: 8, color: PRIMARY }} />
                Top Lanes — Network Overview
                <span style={{ fontSize: 12, color: "#999", fontWeight: 400, marginLeft: 8 }}>Click to drill down</span>
              </span>
            }
          >
            <Table
              columns={topTableColumns}
              dataSource={topLanes}
              rowKey={(r, i) => (r.origin || "") + "_" + (r.destination || "") + "_" + i}
              pagination={{ defaultPageSize: 10, showSizeChanger: true }}
              size="middle"
              scroll={{ x: 800 }}
              style={{ cursor: "pointer" }}
              locale={{ emptyText: <Empty description="No lane data available" /> }}
            />
          </Card>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {showTopDestinations && (
              <Col xs={24} lg={12}>
                <Card
                  style={cardStyle}
                  bodyStyle={{ padding: "16px" }}
                  title={<span style={{ fontWeight: 600, color: "#333", fontSize: 14 }}>Top Destinations</span>}
                >
                  <Table columns={miniLocCols} dataSource={topDestinations} rowKey={(r, i) => "d_" + i}
                    pagination={false} size="small" scroll={{ y: 300 }} />
                </Card>
              </Col>
            )}
            {showTopOrigins && (
              <Col xs={24} lg={12}>
                <Card
                  style={cardStyle}
                  bodyStyle={{ padding: "16px" }}
                  title={<span style={{ fontWeight: 600, color: "#333", fontSize: 14 }}>Top Origins</span>}
                >
                  <Table columns={miniLocCols} dataSource={topOrigins} rowKey={(r, i) => "o_" + i}
                    pagination={false} size="small" scroll={{ y: 300 }} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      );
    }

    return (
      <Card
        style={{ ...cardStyle, marginBottom: 24 }}
        bodyStyle={{ padding: "20px" }}
        title={
          <span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}>
            <FiMapPin style={{ marginRight: 8, color: PRIMARY }} />
            {topTableTitle}
            <span style={{ fontSize: 12, color: "#999", fontWeight: 400, marginLeft: 8 }}>Click to drill down</span>
          </span>
        }
      >
        <Table
          columns={topTableColumns}
          dataSource={topTableData}
          rowKey={(r, i) => (r.location || "") + "_" + i}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          size="middle"
          scroll={{ x: 600 }}
          locale={{ emptyText: <Empty description="No data available" /> }}
        />
      </Card>
    );
  };

  const renderRateLookup = () => {
    // We no longer gate the Fetch Rates button on having a postal code, because
    // the backend can now resolve "City, ST" / state / city inputs directly via
    // KmaReferenceList. As long as the user typed BOTH origin and destination,
    // they can click Fetch Rates — including when there are zero loads in history.
    const originZip = data?.originZip || "";
    const destZip = data?.destinationZip || "";
    const hasZips = originZip && destZip;
    const hasBothInputs = origin.trim() && destination.trim();

    return (
      <Card
        style={{ ...cardStyle, marginBottom: 24 }}
        bodyStyle={{ padding: "24px" }}
        title={
          <span style={{ fontWeight: 600, color: "#333", fontSize: 15 }}>
            <FiDollarSign style={{ marginRight: 8, color: PRIMARY }} />
            SONAR Market Rates
          </span>
        }
      >
        {!hasBothInputs ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <FiMapPin size={36} color="#ccc" style={{ marginBottom: 12 }} />
            <p style={{ color: "#999", margin: 0, fontSize: 14 }}>
              Enter both origin and destination to look up market rates
            </p>
          </div>
        ) : !hasZips && !rateData ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Button
              type="primary"
              size="large"
              onClick={fetchRates}
              loading={rateLoading}
              style={{
                backgroundColor: PRIMARY,
                borderColor: PRIMARY,
                borderRadius: 8,
                fontWeight: 600,
                height: 44,
                paddingLeft: 32,
                paddingRight: 32,
              }}
              icon={<FiDollarSign />}
            >
              Fetch Rates
            </Button>
            <p style={{ color: "#999", marginTop: 12, fontSize: 13 }}>
              Click to fetch SONAR market rates for this lane
            </p>
          </div>
        ) : rateData ? (
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Rate</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: SUCCESS }}>
                  ${formatNumber(rateData.rate)}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>RPM</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: PRIMARY }}>
                  ${rateData.rpm ? Number(rateData.rpm).toFixed(2) : "N/A"}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Rate Range</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#555" }}>
                  {rateData.rate_low && rateData.rate_high
                    ? `$${formatNumber(rateData.rate_low)} - $${formatNumber(rateData.rate_high)}`
                    : "N/A"}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Confidence</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  {rateData.confidence_score !== undefined && rateData.confidence_score !== null ? (
                    <Tag
                      color={
                        rateData.confidence_score >= 70 || rateData.confidence_score >= 4
                          ? "green"
                          : rateData.confidence_score >= 40 || rateData.confidence_score >= 2
                          ? "gold"
                          : "red"
                      }
                      style={{ fontSize: 16, padding: "2px 12px" }}
                    >
                      {rateData.confidence_score <= 5
                        ? `${rateData.confidence_score}/5`
                        : `${Math.round(rateData.confidence_score)}%`}
                    </Tag>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Equipment</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#555" }}>
                  {rateData.equipment_type || "Van"}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 12, color: "#999", marginBottom: 4 }}>Load Count</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#555" }}>
                  {formatNumber(rateData.total_load_count || rateData.load_count)}
                </div>
              </div>
            </Col>
            {(rateData.data_timestamp || rateData.last_updated) && (
              <Col xs={24}>
                <div style={{ textAlign: "right", fontSize: 12, color: "#bbb", marginTop: 4 }}>
                  Last updated:{" "}
                  {new Date(rateData.data_timestamp || rateData.last_updated).toLocaleDateString(
                    "en-US",
                    { month: "short", day: "numeric", year: "numeric" }
                  )}
                </div>
              </Col>
            )}
          </Row>
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Button
              type="primary"
              size="large"
              onClick={fetchRates}
              loading={rateLoading}
              style={{
                backgroundColor: PRIMARY,
                borderColor: PRIMARY,
                borderRadius: 8,
                fontWeight: 600,
                height: 44,
                paddingLeft: 32,
                paddingRight: 32,
              }}
              icon={<FiDollarSign />}
            >
              Fetch Rates
            </Button>
          </div>
        )}

        {rateError && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 16px",
              borderRadius: 8,
              backgroundColor: "rgba(231,76,60,0.08)",
              color: DANGER,
              fontSize: 13,
            }}
          >
            {rateError}
          </div>
        )}
      </Card>
    );
  };

  // ========== MAIN RENDER ==========
  return (
    <Layout2 title="Lane Intelligence">
      <div className="mainareas">
        <div className="edit-page-common">
          <div className="white-bg-main" style={{ padding: 24 }}>
            {/* Page Header */}
            <div
              className="d-flex align-items-center justify-content-between mb-4 flex-wrap"
              style={{ gap: 12 }}
            >
              <div>
                <h3 className="mb-1" style={{ color: PRIMARY, fontWeight: 700, fontSize: 22 }}>
                  Lane Intelligence
                </h3>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  Analyze lane frequency patterns, posting predictions, and market rates
                </p>
              </div>
            </div>

            {/* Search Section */}
            <Card
              style={{ ...cardStyle, marginBottom: 24 }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={12} md={6}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      Origin <span style={{ color: "#bbb", fontWeight: 400 }}>(optional)</span>
                    </label>
                  </div>
                  <Input
                    placeholder="City, state, or ZIP — leave empty for all"
                    value={origin}
                    onChange={(e) => setOrigin(e.target.value)}
                    onKeyDown={handleKeyPress}
                    size="large"
                    prefix={<FiMapPin style={{ color: "#bbb" }} />}
                    style={{ borderRadius: 8 }}
                    allowClear
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>
                      Destination <span style={{ color: "#bbb", fontWeight: 400 }}>(optional)</span>
                    </label>
                  </div>
                  <Input
                    placeholder="City, state, or ZIP — leave empty for all"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    onKeyDown={handleKeyPress}
                    size="large"
                    prefix={<FiMapPin style={{ color: "#bbb" }} />}
                    style={{ borderRadius: 8 }}
                    allowClear
                  />
                </Col>
                <Col xs={12} sm={8} md={3}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>Board</label>
                  </div>
                  <Select
                    placeholder="All Boards"
                    allowClear
                    style={{ width: "100%" }}
                    value={selectedBoard || undefined}
                    onChange={(val) => setSelectedBoard(val || "")}
                    size="large"
                  >
                    {boards.map((b) => (
                      <Option key={b.id || b._id} value={b.id || b._id}>
                        {b.name || b.board_name || b.id}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>From</label>
                  </div>
                  <DatePicker
                    value={fromDate}
                    onChange={(d) => { setFromDate(d); setActivePreset(null); }}
                    format="MM/DD/YYYY"
                    size="large"
                    style={{ borderRadius: 8, width: "100%" }}
                    allowClear
                    placeholder="Start date"
                  />
                </Col>
                <Col xs={12} sm={6} md={3}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>To</label>
                  </div>
                  <DatePicker
                    value={toDate}
                    onChange={(d) => { setToDate(d); setActivePreset(null); }}
                    format="MM/DD/YYYY"
                    size="large"
                    style={{ borderRadius: 8, width: "100%" }}
                    allowClear
                    placeholder="End date"
                  />
                </Col>
                <Col xs={24} sm={8} md={4}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "transparent" }}>&nbsp;</label>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      id="lane-search-btn"
                      type="primary"
                      size="large"
                      onClick={handleSearch}
                      loading={loading}
                      style={{
                        backgroundColor: PRIMARY,
                        borderColor: PRIMARY,
                        borderRadius: 8,
                        fontWeight: 600,
                        flex: 1,
                        height: 40,
                      }}
                      icon={<FiSearch />}
                    >
                      Search
                    </Button>
                    {searched && (
                      <Button
                        size="large"
                        onClick={handleClear}
                        style={{
                          borderRadius: 8,
                          height: 40,
                        }}
                        icon={<FiXCircle />}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                <span style={{ fontSize: 12, color: "#999", lineHeight: "28px", marginRight: 4 }}>Quick:</span>
                {PERIOD_PRESETS.map((p) => (
                  <Button
                    key={p.label}
                    size="small"
                    type={activePreset === p.label ? "primary" : "default"}
                    style={{
                      borderRadius: 6,
                      fontWeight: 500,
                      fontSize: 12,
                      height: 28,
                      ...(activePreset === p.label ? { backgroundColor: PRIMARY, borderColor: PRIMARY } : {}),
                    }}
                    onClick={() => {
                      setActivePreset(p.label);
                      const today = dayjs();
                      setToDate(today);
                      if (p.key === "lastWeek") {
                        setFromDate(dayjs().startOf("isoWeek").subtract(1, "week"));
                        setToDate(dayjs().startOf("isoWeek").subtract(1, "day"));
                      } else if (p.key === "lastMonth") {
                        setFromDate(dayjs().subtract(1, "month").startOf("month"));
                        setToDate(dayjs().subtract(1, "month").endOf("month"));
                      } else {
                        setFromDate(dayjs().subtract(p.days, "day"));
                        setToDate(today);
                      }
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </Card>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: "center", padding: "80px 0" }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: "#999", fontSize: 15 }}>
                  Analyzing lane patterns...
                </p>
              </div>
            )}

            {/* Error State */}
            {!loading && searched && error && !data && (
              <>
                <Card style={{ ...cardStyle, marginBottom: 24 }} bodyStyle={{ padding: "48px 24px" }}>
                  <Empty
                    description={
                      <span style={{ color: "#999", fontSize: 15 }}>
                        {error}
                      </span>
                    }
                  />
                </Card>

                {/*
                  Even when there are NO loads in history for this lane, we still
                  want the user to be able to pull SONAR market rates. Render the
                  Rate Lookup card here so the "Fetch Rates" button is reachable
                  whenever both origin and destination were entered.
                */}
                {origin.trim() && destination.trim() && renderRateLookup()}
              </>
            )}

            {/* Results */}
            {!loading && data && (
              <>
                {/* Row 1: Summary Cards */}
                {renderSummaryCards()}

                {/* Row 2: Posting Probability */}
                {renderPostingProbability()}

                {/* Row 3: Day & Hour Charts */}
                {renderCharts()}

                {/* Monthly Trend removed per user request */}

                {/* Row 4: Recurring Pickup Schedules */}
                {renderSchedulePatterns()}

                {/* Schedule Loads Modal — shows loads for a specific day of a pattern */}
                <Modal
                  title={
                    scheduleLoadsModal && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                          {scheduleLoadsModal.dayName} pickups at {scheduleLoadsModal.sp?.pickupTime}
                        </span>
                        {scheduleLoadsModal.sp?.lane && (
                          <Tag color={PRIMARY} style={{ fontSize: 12 }}>{scheduleLoadsModal.sp.lane}</Tag>
                        )}
                      </div>
                    )
                  }
                  open={!!scheduleLoadsModal}
                  onCancel={() => setScheduleLoadsModal(null)}
                  footer={null}
                  width={780}
                  bodyStyle={{ padding: "12px 20px", maxHeight: "70vh", overflowY: "auto" }}
                >
                  {scheduleLoadsModal && (
                    scheduleLoadsModal.loading ? (
                      <div style={{ textAlign: "center", padding: 40 }}>
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: 22, color: PRIMARY }}></i>
                      </div>
                    ) : scheduleLoadsModal.loads.length === 0 ? (
                      <Empty description="No loads found matching this schedule" />
                    ) : (
                      <Table
                        columns={[
                          {
                            title: "Load ID",
                            dataIndex: "load_id",
                            key: "load_id",
                            width: 110,
                            render: (v) => <span style={{ fontWeight: 600, fontFamily: "monospace", fontSize: 12, color: PRIMARY }}>{v}</span>,
                          },
                          {
                            title: "Pickup Date",
                            dataIndex: "pickupDate",
                            key: "pickupDate",
                            render: (v) => v ? new Date(v).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "N/A",
                            sorter: (a, b) => new Date(a.pickupDate || 0) - new Date(b.pickupDate || 0),
                            defaultSortOrder: "descend",
                          },
                          {
                            title: "Route",
                            key: "route",
                            render: (_, r) => <span style={{ fontSize: 12 }}>{r.origin} → {r.destination}</span>,
                          },
                          {
                            title: "Distance",
                            dataIndex: "distance",
                            key: "distance",
                            width: 80,
                            render: (v) => v ? `${Math.round(v)} mi` : "—",
                          },
                          {
                            title: "Rate",
                            dataIndex: "rate",
                            key: "rate",
                            width: 90,
                            render: (v) => v ? `$${Number(v).toLocaleString()}` : "—",
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            width: 100,
                            render: (v) => {
                              const colors = { delivered: SUCCESS, pending: WARNING, intransit: PRIMARY, pickedup: "#17a2b8" };
                              return <Tag color={colors[v] || "#999"} style={{ fontSize: 10 }}>{(v || "—").toUpperCase()}</Tag>;
                            },
                          },
                        ]}
                        dataSource={scheduleLoadsModal.loads}
                        rowKey={(r) => r._id}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        onRow={(record) => ({
                          onClick: () => {
                            setScheduleLoadsModal(null);
                            openLoadModal(record);
                          },
                          style: { cursor: "pointer" },
                        })}
                      />
                    )
                  )}
                </Modal>

                {/* Row 5: Top Destinations/Origins */}
                {renderTopTable()}

                {/* Row 6: Recent Loads History */}
                {renderRecentLoads()}

                {/* Row 7: Rate Lookup */}
                {renderRateLookup()}
              </>
            )}

            {/* Initial empty state */}
            {!searched && !loading && (
              <Card style={{ ...cardStyle }} bodyStyle={{ padding: "64px 24px" }}>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: "rgba(62,84,157,0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                    }}
                  >
                    <FiSearch size={32} color={PRIMARY} />
                  </div>
                  <h4 style={{ color: "#333", fontWeight: 600, marginBottom: 8 }}>
                    Search a Lane to Get Started
                  </h4>
                  <p style={{ color: "#999", maxWidth: 480, margin: "0 auto", lineHeight: 1.6 }}>
                    Enter an origin city, state, or ZIP code above to analyze historical posting
                    patterns, frequency trends, and market rates for any lane.
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout2>
  );
};

export default LaneIntelligence;
